import React, { useEffect } from 'react'
import { FormGroup, FormControl, HelpBlock } from 'react-bootstrap'

import Label from 'live/components/forms/ui/label'
import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { EntityEditorModal } from '../../../../../generic'
import { checkIsValidFrequencyRange } from '../../../../../utils'
import { IDiagramConsequence, IDiagramThreat } from '../../RiskDiagramEditor'

type IProps = {
    handleClose: () => void
    handleDescriptionChange: (e: string) => void
    handleInitialFrequencyChange?: (e: string) => void
    handleThresholdFrequencyChange?: (e: string) => void
    isOpen: boolean
    item: IDiagramThreat | IDiagramConsequence
    borderColor: string
    title: string
}

export const ThreatConsequenceModal: React.FC<IProps> = ({
    handleClose,
    isOpen,
    item,
    handleDescriptionChange,
    handleInitialFrequencyChange,
    handleThresholdFrequencyChange,
    borderColor,
    title
}) => {
    const [stepCount, setStepCount] = React.useState<string>(undefined)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    const stepCountFunction = (count: number): void => {
        if (count <= 3 || undefined) {
            setStepCount('0.1')
        } else {
            let aux = ''
            aux = aux.padStart(count - 3, '0')
            setStepCount('0.' + aux + '1')
        }
    }

    useEffect(() => {
        if (title.toUpperCase() === 'THREAT') {
            const count = item && (item as IDiagramThreat).initialFrequency && (item as IDiagramThreat).initialFrequency
            if (count) {
                stepCountFunction(String(count).length)
            }
        }
    }, [item]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <EntityEditorModal
            handleClose={handleClose}
            isOpen={isOpen}
            borderColor={borderColor}
            headerTitle={i18n(title)}
            headerSubtitle={item?.id ? '#' + item.id : ''}
        >
            <FormGroup
                validationState={
                    title.toUpperCase() === 'THREAT'
                        ? (item as IDiagramThreat)?.name != null && (item as IDiagramThreat)?.name?.length > 0
                            ? 'success'
                            : 'error'
                        : (item as IDiagramConsequence)?.name != null && (item as IDiagramConsequence)?.name?.length > 0
                        ? 'success'
                        : 'error'
                }
            >
                <Label required className="control-label">
                    {i18n('Name')}
                </Label>
                {bowtieAdmin ? (
                    <FormControl
                        type="text"
                        componentClass="textarea"
                        style={{ height: 80, maxHeight: 80, minHeight: 80 }}
                        onChange={e => handleDescriptionChange((e.target as HTMLInputElement).value)}
                        placeholder={i18n(`NEW ${title.toUpperCase()} NAME`)}
                        id="diagramEditor-threatConsequenceModal-field-name"
                        value={item?.name}
                        maxLength={255}
                    />
                ) : (
                    <FormControl
                        readOnly
                        type="text"
                        componentClass="textarea"
                        style={{ height: 80, maxHeight: 80, minHeight: 80 }}
                        onChange={e => handleDescriptionChange((e.target as HTMLInputElement).value)}
                        placeholder={i18n(`NEW ${title.toUpperCase()} NAME`)}
                        id="diagramEditor-threatConsequenceModal-field-name"
                        value={item?.name}
                        maxLength={255}
                    />
                )}
            </FormGroup>

            {title.toUpperCase() === 'THREAT' ? (
                <FormGroup
                    validationState={
                        checkIsValidFrequencyRange((item as IDiagramThreat)?.initialFrequency) ? 'success' : 'error'
                    }
                >
                    <Label required className="control-label">
                        {i18n('Initial Frequency')}
                    </Label>
                    {bowtieAdmin ? (
                        <FormControl
                            type="number"
                            min={0}
                            max={1}
                            step={stepCount}
                            onChange={e => {
                                const count = (e.target as HTMLInputElement).value
                                handleInitialFrequencyChange(count)
                                stepCountFunction(count.length)
                            }}
                            id="diagramEditor-threatConsequenceModal-field-initialFrequency"
                            value={(item as IDiagramThreat)?.initialFrequency}
                            placeholder={i18n('INITIAL FREQUENCY VALUE')}
                        />
                    ) : (
                        <FormControl
                            readOnly
                            type="number"
                            min={0}
                            max={1}
                            step={stepCount}
                            onChange={e => {
                                const count = (e.target as HTMLInputElement).value
                                handleInitialFrequencyChange(count)
                                stepCountFunction(count.length)
                            }}
                            id="diagramEditor-threatConsequenceModal-field-initialFrequency"
                            value={(item as IDiagramThreat)?.initialFrequency}
                            placeholder={i18n('INITIAL FREQUENCY VALUE')}
                        />
                    )}
                    <HelpBlock>{i18n('Value Between 0 and 1')} </HelpBlock>
                    <HelpBlock>{i18n('For a semi-qualitative bowtie, use Initial Freq. with value 1')} </HelpBlock>
                </FormGroup>
            ) : (
                <FormGroup
                    validationState={
                        checkIsValidFrequencyRange((item as IDiagramConsequence)?.thresholdFrequency)
                            ? 'success'
                            : 'error'
                    }
                >
                    <Label required className="control-label">
                        {i18n('Threshold Frequency')}
                    </Label>
                    {bowtieAdmin ? (
                        <FormControl
                            type="number"
                            min={0}
                            max={1}
                            step={stepCount}
                            onChange={e => {
                                const count = (e.target as HTMLInputElement).value
                                handleThresholdFrequencyChange((e.target as HTMLInputElement).value)
                                stepCountFunction(count.length)
                            }}
                            id="diagramEditor-threatConsequenceModal-field-thresholdFrequency"
                            value={(item as IDiagramConsequence)?.thresholdFrequency}
                            placeholder={i18n('THRESHOLD FREQUENCY VALUE')}
                        />
                    ) : (
                        <FormControl
                            readOnly
                            type="number"
                            min={0}
                            max={1}
                            step={stepCount}
                            onChange={e => {
                                const count = (e.target as HTMLInputElement).value
                                handleThresholdFrequencyChange((e.target as HTMLInputElement).value)
                                stepCountFunction(count.length)
                            }}
                            id="diagramEditor-threatConsequenceModal-field-thresholdFrequency"
                            value={(item as IDiagramConsequence)?.thresholdFrequency}
                            placeholder={i18n('THRESHOLD FREQUENCY VALUE')}
                        />
                    )}
                    <HelpBlock>{i18n('Value Between 0 and 1')} </HelpBlock>
                    <HelpBlock>{i18n('For a semi-qualitative bowtie, use Threshold Freq. with value 1')} </HelpBlock>
                </FormGroup>
            )}
        </EntityEditorModal>
    )
}
